import createClient, { Middleware } from 'openapi-fetch';
import type { paths as AdminPaths } from '@/@types/openapi-admin';
import type { paths as AppPaths } from '@/@types/openapi-app';
import { showToast } from './utils';

/**
 * Unified API client that supports both admin and app schemas
 * Provides type-safe methods for both /api/admin and /api/app endpoints
 */

// Helper function to get cookie value
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue || null;
  }
  return null;
}

// Common middleware for both clients
export const middleware: Middleware = {
  // async onRequest({ request, options: _ }) {
  //   // Try to use Bearer token if available, otherwise rely on cookies
  //   // const accessToken = localStorage.getItem('access_token');
  //   // if (accessToken) {
  //   //   request.headers.set('Authorization', `Bearer ${accessToken}`);
  //   // }
  //   // Note: HttpOnly cookies are automatically included due to credentials: 'include'
  //   return request;
  // },
  async onResponse({
    response,
  }: {
    response: Response & { error?: { message: string; code: string } };
  }) {
    if (response?.error || response?.status !== 200) {
      const message = response?.error?.message || response?.statusText;
      showToast(message);

      if (response?.error?.code === 'ERR_BAD_REQUEST') {
        showToast('Bad request');
      }
      if (
        response?.error?.message === 'Network Error' ||
        response?.error?.code === 'ECONNABORTED'
      ) {
        showToast(`No Connection`);
      }
      throw new Error(response?.error?.message || 'Unknown error');
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { body, ...resOptions } = response;
    return new Response(response?.body, { ...resOptions, status: 200 });
  },
};

// Create separate clients for admin and app
const adminClient = createClient<AdminPaths>({
  baseUrl: `${window.location.origin}/api/admin`,
  // Include cookies for authentication
  credentials: 'include',
});

const appClient = createClient<AppPaths>({
  baseUrl: `${window.location.origin}/api/app`,
  // Include cookies for authentication
  credentials: 'include',
});

// Apply middleware to both clients
adminClient.use(middleware);
appClient.use(middleware);

/**
 * Unified client interface that provides access to both admin and app APIs
 */
export class UnifiedAPIClient {
  /**
   * Access to admin API endpoints (/api/admin/*)
   * Uses AdminPaths types for full type safety
   */
  get admin() {
    return adminClient;
  }

  /**
   * Access to app API endpoints (/api/app/*)
   * Uses AppPaths types for full type safety
   */
  get app() {
    return appClient;
  }

  /**
   * Generic method to call any endpoint with automatic base URL detection
   * @param path - The API path (should start with /admin/ or /app/)
   * @param options - Request options
   */
  async request<T = any>(path: string, options: RequestInit = {}): Promise<T> {
    let baseUrl: string;
    let cleanPath: string;
    
    if (path.startsWith('/admin/')) {
      baseUrl = `${window.location.origin}/api/admin`;
      cleanPath = path.substring(7); // Remove '/admin/'
    } else if (path.startsWith('/app/')) {
      baseUrl = `${window.location.origin}/api/app`;
      cleanPath = path.substring(5); // Remove '/app/'
    } else {
      throw new Error('Path must start with /admin/ or /app/');
    }

    const url = `${baseUrl}/${cleanPath}`;
    
    const headers = new Headers(options.headers);
    // if (accessToken) {
    //   headers.set('Authorization', `Bearer ${accessToken}`);
    // }
    
    // Add ss_admin cookie as fallback authentication method
    const adminCookie = getCookie('ss_admin');
    if (adminCookie) {
      headers.set('X-Admin-Session', adminCookie);
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for authentication
    });

    if (!response.ok) {
      const errorMessage = `HTTP error! status: ${response.status}`;
      showToast(errorMessage);
      throw new Error(errorMessage);
    }

    return response.json();
  }
}

// Export singleton instance
export const unifiedClient = new UnifiedAPIClient();

// For backward compatibility, export the admin client as the default client
export const client = adminClient;

// Export individual clients for direct access if needed
export const adminApiClient = adminClient;
export const appApiClient = appClient;
